import React from 'react';
import Typography from '@/components/atoms/project-builder/typography';
import RoomsDetails from './roomsDetails';
import Dropdown from '@/components/atoms/project-builder/dropdown';
import Input from '@/components/atoms/project-builder/input';

const TextInputField: React.FC<any> = ({
    name,
    placeholder,
    value,
    error,
    onChange,
    onPaste,
    onKeyDown,
    maxLength = 50,
}) => (
    <Input
        name={name}
        placeholder={placeholder}
        value={value ?? ''}
        error={error}
        onChange={onChange}
        onPaste={onPaste}
        onKeyDown={onKeyDown}
        maxLength={maxLength}
        autoComplete=""
        tabIndex={0}
                        className={` !h-[40px] mb-[20px] !p-[13px] !text-[14px] placeholder:!text-[14px] !font-medium ${error ? '!border-red-500' : '!border-[#A7A7A7]'} !rounded-md`}
        errorClassName="!absolute top-[30px] left-[4px]"
    />
);

const DropdownField: React.FC<any> = ({
    name,
    options,
    value,
    error,
    onChange,
}) => (
    <div className="relative projectDropDown !mb-[20px] md:w-[250px]">
        <Dropdown
            selectOptions={options}
            selectOnChange={(val) => onChange(name, val)}
            defaultValue={value ?? options[0]}
            disableFirstOption
            clearDropdown={false}
            error={error}
            optionsClassName="!top-9 xl:!top-9.5 md:!top-9.5 border-light-gray"
            selectDropdownClassNames={`${!value ? 'text-secondary-black' : '!text-black'
                } !rounded-md !text-[14px] border-light-gray text-left overflow-hidden !h-fit !px-3 !py-2 w-full`}
        />
        {error && (
            <Typography
                className="text-error-600 !absolute left-[4px] top-[30px] mt-[12px] text-[12px] font-medium leading-[16px]"
                as="span"
            >
                {error}
            </Typography>
        )}
    </div>
);

const SpecifierSection: React.FC<any> = ({
    formData,
    formErrors,
    onChange,
    onPaste,
    onKeyDown,
}) => (
    <div>
        <Typography className="fontSize18-sm !mb-[20px] text-black">
            Enter your name
        </Typography>
                <div className="padding-r-38-lg grid gap-[6px] md:flex">
          <div className="w-full md:w-1/2 lg:w-full lg:max-w-[385px]">
                <TextInputField
                    name="username"
                    placeholder={'Username*'}
                    value={formData.username}
                    error={formErrors.username}
                    onChange={onChange}
                    onPaste={(e: any) => onPaste?.(e, 'username')}
                    onKeyDown={(e: any) => onKeyDown?.(e, 'username')}
                />
            </div>
        </div>
    </div>
);

const CustomerSection: React.FC<any> = ({
    formData,
    formErrors,
    onChange,
    onPaste,
    onKeyDown,
}) => {
    return (
        <div className="mt-[20px] aboutCustomer-container">
            <Typography className="!mb-[20px] fontSize18-sm text-black">
                About the customer
            </Typography>
            <div className="padding-r-38-lg aboutProject grid gap-6 md:flex">
                {['customerName', 'customerEmail', 'customerPhone'].map((field) => (
                    <div key={field} className="aboutProjectFlex md:w-[250px]">
                        <TextInputField
                            name={field}
                            placeholder={`${field.replace('customer', 'Customer ')}`}
                            value={formData[field]}
                            error={formErrors[field]}
                            onChange={onChange}
                            onPaste={(e: any) => onPaste?.(e, field)}
                            onKeyDown={(e: any) => onKeyDown?.(e, field)}
                            maxLength={field === 'customerPhone' ? 12 : 50}
                        />
                    </div>
                ))}
            </div>
            <div className="w-full mt-5 lg:w-[60%]">
                <div className="aboutProjectFlex">
                    <TextInputField
                        name="customerAddress"
                        placeholder={'Customer Address'}
                        value={formData.customerAddress}
                        error={formErrors.customerAddress}
                        onChange={onChange}
                        onPaste={(e: any) => onPaste?.(e, 'customerAddress')}
                        onKeyDown={(e: any) => onKeyDown?.(e, 'customerAddress')}
                        maxLength={200}
                    />
                </div>
            </div>
        </div>
    );
};

const ProjectMetaSection: React.FC<any> = ({
    formData,
    formErrors,
    onChange,
    onDropdownChange,
    options,
    isEdit,
}) => {
    const renderInput = (name: string, placeholder: string) => (
        <div className="aboutProjectFlex md:w-[250px]" key={name}>
            <TextInputField
                name={name}
                placeholder={placeholder}
                value={formData[name] ?? ''}
                error={formErrors[name]}
                onChange={(e: any) => onChange(e, name)}
                onPaste={(e: any) => onChange(e, name)}
                onKeyDown={(e: any) => onChange(e, name)}
            />
        </div>
    );

    const renderDropdown = (name: string, opts: any[]) => (
        <div className="aboutProjectFlex" key={name}>
            <DropdownField
                name={name}
                options={opts}
                value={formData[name]}
                error={formErrors[name]}
                onChange={onDropdownChange}
            />
        </div>
    );

    return (
        <div className="aboutProject-container">
            <Typography className="fontSize18-sm !mb-[20px] text-black">
                About the project
            </Typography>

            <div className="padding-r-38-lg aboutProject grid gap-6 md:flex">
                {renderInput('opportunityNumber', 'Project ID')}
                {renderInput('projectName', 'Project name*')}
                <span className="mb-6 md:mb-0">
                    {renderDropdown('projectType', options.projectType)}
                </span>
            </div>

            <div className="padding-r-38-lg aboutProject grid gap-6 md:flex">
                {renderDropdown('projectLocation', options.projectLocation)}
                {isEdit && renderDropdown('projectStatus', options.projectStatus)}
            </div>
        </div>
    );
};

const RoomsSection: React.FC<any> = ({
    selectedRoomsFromParent,
    onSelectedRoomsChange,
    disabled,
    showError,
    setIsFormDirty,
}) => {
    if (disabled) return null;

    return (
        <div className="mt-[20px]">
            <Typography className="fontSize18-sm !mb-[20px] text-black">
                Rooms in this project
            </Typography>
            <RoomsDetails
                onSelectedRoomsChange={onSelectedRoomsChange}
                selectedRoomsParent={selectedRoomsFromParent}
                disabled={disabled}
                setIsFormDirty={setIsFormDirty}
            />
            {showError && (
                <Typography
                    className="!absolute mt-[12px] text-[12px] font-medium leading-[16px] text-red-500"
                    as="span"
                >
                    {'Minimum one room is required.'}
                </Typography>
            )}
        </div>
    );
};

export {
    TextInputField,
    DropdownField,
    SpecifierSection,
    CustomerSection,
    ProjectMetaSection,
    RoomsSection,
};
